import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { subscriptionsAPI } from '../services/api';

// Get today's date in local timezone
const getTodayLocal = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Initial state
const initialState = {
  selectedDate: getTodayLocal(), // Today in YYYY-MM-DD format using local timezone
  rooms: [],
  orders: [],
  users: [],
  establishments: [],
  isLoading: false,
  error: null,
  filters: {
    showWithOrders: null, // null = all, true = with orders, false = without orders
    assignedStaff: null,
    cleaningType: null,
    status: null,
  },
  notifications: [],
  isMobile: window.innerWidth <= 1024,
  planFeatures: {
    incident_management: true,
    custom_cleaning_types: true,
    custom_bed_dispositions: true,
  },
};

// Action types
const APP_ACTIONS = {
  SET_SELECTED_DATE: 'SET_SELECTED_DATE',
  SET_ROOMS: 'SET_ROOMS',
  SET_ORDERS: 'SET_ORDERS',
  SET_USERS: 'SET_USERS',
  SET_ESTABLISHMENTS: 'SET_ESTABLISHMENTS',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_FILTERS: 'SET_FILTERS',
  RESET_FILTERS: 'RESET_FILTERS',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
  SET_IS_MOBILE: 'SET_IS_MOBILE',
  UPDATE_ROOM: 'UPDATE_ROOM',
  UPDATE_ORDER: 'UPDATE_ORDER',
  ADD_ORDER: 'ADD_ORDER',
  REMOVE_ORDER: 'REMOVE_ORDER',
  SET_PLAN_FEATURES: 'SET_PLAN_FEATURES',
};

// Reducer
const appReducer = (state, action) => {
  switch (action.type) {
    case APP_ACTIONS.SET_SELECTED_DATE:
      return {
        ...state,
        selectedDate: action.payload,
      };

    case APP_ACTIONS.SET_ROOMS:
      return {
        ...state,
        rooms: action.payload,
      };

    case APP_ACTIONS.SET_ORDERS:
      return {
        ...state,
        orders: action.payload,
      };

    case APP_ACTIONS.SET_USERS:
      return {
        ...state,
        users: action.payload,
      };

    case APP_ACTIONS.SET_ESTABLISHMENTS:
      return {
        ...state,
        establishments: action.payload,
      };

    case APP_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case APP_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case APP_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case APP_ACTIONS.SET_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload,
        },
      };

    case APP_ACTIONS.RESET_FILTERS:
      return {
        ...state,
        filters: {
          showWithOrders: null,
          assignedStaff: null,
          cleaningType: null,
          status: null,
        },
      };

    case APP_ACTIONS.ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [...state.notifications, action.payload],
      };

    case APP_ACTIONS.REMOVE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload),
      };

    case APP_ACTIONS.SET_IS_MOBILE:
      return {
        ...state,
        isMobile: action.payload,
      };

    case APP_ACTIONS.UPDATE_ROOM:
      return {
        ...state,
        rooms: state.rooms.map(room =>
          room.id === action.payload.id ? { ...room, ...action.payload } : room
        ),
      };

    case APP_ACTIONS.UPDATE_ORDER:
      return {
        ...state,
        orders: state.orders.map(order =>
          order.id === action.payload.id ? { ...order, ...action.payload } : order
        ),
      };

    case APP_ACTIONS.ADD_ORDER:
      return {
        ...state,
        orders: [...state.orders, action.payload],
      };

    case APP_ACTIONS.REMOVE_ORDER:
      return {
        ...state,
        orders: state.orders.filter(order => order.id !== action.payload),
      };

    case APP_ACTIONS.SET_PLAN_FEATURES:
      return {
        ...state,
        planFeatures: action.payload,
      };

    default:
      return state;
  }
};

// Create context
const AppContext = createContext();

// Provider component
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const { user } = useAuth();

  // Load plan features when user is authenticated
  useEffect(() => {
    const loadPlanFeatures = async () => {
      if (user?.establishment_id) {
        try {
          const features = await subscriptionsAPI.getCurrentFeatures();
          dispatch({
            type: APP_ACTIONS.SET_PLAN_FEATURES,
            payload: features,
          });
        } catch (error) {
          console.error('Error loading plan features:', error);
          // Keep default features on error
        }
      }
    };

    loadPlanFeatures();
  }, [user?.establishment_id]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      dispatch({
        type: APP_ACTIONS.SET_IS_MOBILE,
        payload: window.innerWidth <= 1024,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper functions
  const setSelectedDate = (date) => {
    dispatch({
      type: APP_ACTIONS.SET_SELECTED_DATE,
      payload: date,
    });
  };

  const setRooms = (rooms) => {
    dispatch({
      type: APP_ACTIONS.SET_ROOMS,
      payload: rooms,
    });
  };

  const setOrders = (ordersOrUpdater) => {
    // Support both direct values and updater functions (like useState)
    const newOrders = typeof ordersOrUpdater === 'function'
      ? ordersOrUpdater(state.orders)
      : ordersOrUpdater;

    dispatch({
      type: APP_ACTIONS.SET_ORDERS,
      payload: newOrders,
    });
  };

  const setUsers = (users) => {
    dispatch({
      type: APP_ACTIONS.SET_USERS,
      payload: users,
    });
  };

  const setEstablishments = (establishments) => {
    dispatch({
      type: APP_ACTIONS.SET_ESTABLISHMENTS,
      payload: establishments,
    });
  };

  const setLoading = (loading) => {
    dispatch({
      type: APP_ACTIONS.SET_LOADING,
      payload: loading,
    });
  };

  const setError = (error) => {
    dispatch({
      type: APP_ACTIONS.SET_ERROR,
      payload: error,
    });
  };

  const clearError = () => {
    dispatch({ type: APP_ACTIONS.CLEAR_ERROR });
  };

  const setFilters = (filters) => {
    dispatch({
      type: APP_ACTIONS.SET_FILTERS,
      payload: filters,
    });
  };

  const resetFilters = () => {
    dispatch({ type: APP_ACTIONS.RESET_FILTERS });
  };

  const addNotification = (notification) => {
    const id = Date.now() + Math.random();
    dispatch({
      type: APP_ACTIONS.ADD_NOTIFICATION,
      payload: { ...notification, id },
    });

    // Auto-remove notification after 5 seconds
    setTimeout(() => {
      removeNotification(id);
    }, 5000);

    return id;
  };

  const removeNotification = (id) => {
    dispatch({
      type: APP_ACTIONS.REMOVE_NOTIFICATION,
      payload: id,
    });
  };

  const updateRoom = (roomData) => {
    dispatch({
      type: APP_ACTIONS.UPDATE_ROOM,
      payload: roomData,
    });
  };

  const updateOrder = (orderData) => {
    dispatch({
      type: APP_ACTIONS.UPDATE_ORDER,
      payload: orderData,
    });
  };

  const addOrder = (orderData) => {
    dispatch({
      type: APP_ACTIONS.ADD_ORDER,
      payload: orderData,
    });
  };

  const removeOrder = (orderId) => {
    dispatch({
      type: APP_ACTIONS.REMOVE_ORDER,
      payload: orderId,
    });
  };

  // Get filtered rooms based on current filters
  const getFilteredRooms = () => {
    let filtered = [...state.rooms];

    // Filter by orders
    if (state.filters.showWithOrders !== null) {
      filtered = filtered.filter(room => {
        const hasOrders = state.orders.some(order => order.room_id === room.id);
        return state.filters.showWithOrders ? hasOrders : !hasOrders;
      });
    }

    return filtered;
  };

  // Get orders for a specific room
  const getRoomOrders = (roomId) => {
    return state.orders.filter(order => order.room_id === roomId);
  };

  // Get cleaning staff users
  const getCleaningStaff = () => {
    return state.users.filter(user => user.role === 'personal_limpieza');
  };

  // Check if a feature is enabled
  const hasFeature = (featureName) => {
    return state.planFeatures[featureName] !== false;
  };

  // Context value
  const contextValue = {
    ...state,
    setSelectedDate,
    setRooms,
    setOrders,
    setUsers,
    setEstablishments,
    setLoading,
    setError,
    clearError,
    setFilters,
    resetFilters,
    addNotification,
    removeNotification,
    updateRoom,
    updateOrder,
    addOrder,
    removeOrder,
    getFilteredRooms,
    getRoomOrders,
    getCleaningStaff,
    hasFeature,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use app context
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;